/**
 * AIInsights — RAG-powered intelligence search.
 * Ask questions, find similar historical setups, context-aware analysis.
 */
import { useState } from "react";
import { api } from "@/lib/api";

interface SearchResult {
  id: string;
  document: string;
  metadata: {
    symbol: string;
    action: string;
    pnl: number;
    strategy: string;
    sector: string;
    holding_days: number;
    mode: string;
    entry_date: string;
  };
  score: number;
}

interface SetupData {
  symbol: string;
  historicalTrades: number;
  trades: any[];
  recentBars: any[];
  currentPosition: any;
}

export default function AIInsights() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [setupData, setSetupData] = useState<SetupData | null>(null);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState("");
  const [ragStatus, setRagStatus] = useState<any>(null);

  // Check RAG status on mount
  const checkStatus = async () => {
    try {
      const res = await api.get<any>("/rag/status");
      setRagStatus(res);
    } catch {
      setRagStatus({ running: false });
    }
  };

  // Search
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setResults([]);
    setSetupData(null);
    try {
      const res = await api.post<{ source: string; count: number; results: SearchResult[] }>("/rag/query", {
        query: query.trim(),
        topK: 15,
      });
      setResults(res.results || []);
      setSource(res.source || "unknown");
    } catch (err: any) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Quick queries
  const quickQueries = [
    "gap up fill strategy with high volume",
    "momentum breakout trades in banking sector",
    "losing trades in IT sector",
    "quick scalp under 3 days",
    "RELIANCE winning setups",
  ];

  // Symbol lookup
  const [symbolLookup, setSymbolLookup] = useState("");
  const handleSymbolLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!symbolLookup.trim()) return;
    setLoading(true);
    try {
      const res = await api.get<SetupData>(`/rag/setup/${symbolLookup.toUpperCase()}`);
      setSetupData(res);
      setResults([]);
    } catch { setSetupData(null); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ padding: 20, maxWidth: 900, margin: "0 auto" }}>
      <h2 style={{ color: "#e0e0e0", fontSize: 20, marginBottom: 16 }}>AI Insights</h2>

      {/* RAG Status */}
      <div style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "center" }}>
        <span style={{
          fontSize: 12, padding: "3px 10px", borderRadius: 10,
          background: ragStatus?.running ? "#6c5ce730" : "#ff525230",
          color: ragStatus?.running ? "#6c5ce7" : "#ff5252",
        }}>
          RAG: {ragStatus?.running ? "Online" : "Offline"}
        </span>
        {!ragStatus?.running && (
          <button onClick={async () => {
            await api.post("/rag/start");
            setTimeout(checkStatus, 3000);
          }} style={{ fontSize: 12, background: "#6c5ce7", color: "#fff", border: "none", borderRadius: 4, padding: "4px 12px", cursor: "pointer" }}>
            Start RAG
          </button>
        )}
        <button onClick={checkStatus} style={{ fontSize: 12, background: "transparent", color: "#666", border: "1px solid #3a3a4e", borderRadius: 4, padding: "4px 10px", cursor: "pointer" }}>
          Check Status
        </button>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input
          value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Ask: gap up fill, momentum breakout, losing IT trades..."
          style={{ flex: 1, background: "#2b2b3d", color: "#e0e0e0", border: "1px solid #3a3a4e", borderRadius: 6, padding: "10px 14px", fontSize: 14 }}
        />
        <button type="submit" style={{ background: "#6c5ce7", color: "#fff", border: "none", borderRadius: 6, padding: "10px 20px", cursor: "pointer", fontSize: 14 }}>
          Search
        </button>
      </form>

      {/* Quick queries */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {quickQueries.map((q) => (
          <button key={q} onClick={() => { setQuery(q); }} style={{
            background: "#2b2b3d", color: "#888", border: "1px solid #3a3a4e",
            borderRadius: 4, padding: "4px 10px", cursor: "pointer", fontSize: 11,
          }}>
            {q}
          </button>
        ))}
      </div>

      {/* Symbol lookup */}
      <form onSubmit={handleSymbolLookup} style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <input value={symbolLookup} onChange={(e) => setSymbolLookup(e.target.value)}
          placeholder="Lookup symbol setup (e.g. RELIANCE)"
          style={{ background: "#2b2b3d", color: "#e0e0e0", border: "1px solid #3a3a4e", borderRadius: 6, padding: "8px 12px", fontSize: 13, width: 250 }}
        />
        <button type="submit" style={{ background: "#00cec9", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", cursor: "pointer", fontSize: 13 }}>
          Lookup
        </button>
      </form>

      {/* Loading */}
      {loading && <div style={{ color: "#666", padding: 20, textAlign: "center" }}>Searching...</div>}

      {/* Search results */}
      {results.length > 0 && (
        <div>
          <div style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>
            {results.length} results (source: {source})
          </div>
          {results.map((r, i) => (
            <div key={r.id} style={{
              background: "#22223a", borderRadius: 8, padding: "12px 16px", marginBottom: 8,
              borderLeft: `3px solid ${r.metadata.pnl >= 0 ? "#6c5ce7" : "#ff5252"}`,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ color: "#e0e0e0", fontWeight: 600 }}>{r.metadata.symbol}</span>
                  <span style={{
                    background: r.metadata.action === "BUY" ? "#6c5ce730" : "#ff525230",
                    color: r.metadata.action === "BUY" ? "#6c5ce7" : "#ff5252",
                    padding: "2px 8px", borderRadius: 4, fontSize: 11,
                  }}>
                    {r.metadata.action}
                  </span>
                  <span style={{ color: "#666", fontSize: 11 }}>{r.metadata.strategy}</span>
                </div>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <span style={{ color: r.metadata.pnl >= 0 ? "#6c5ce7" : "#ff5252", fontWeight: 600, fontSize: 13 }}>
                    {r.metadata.pnl >= 0 ? "+" : ""}Rs{r.metadata.pnl?.toFixed(0)}
                  </span>
                  <span style={{ color: "#00cec9", fontSize: 11 }}>Score: {(r.score * 100).toFixed(0)}%</span>
                </div>
              </div>
              <p style={{ margin: 0, fontSize: 12, color: "#a0a0b0", lineHeight: 1.5 }}>
                {r.document}
              </p>
              <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
                <span style={{ fontSize: 10, color: "#555" }}>Entry: {r.metadata.entry_date}</span>
                <span style={{ fontSize: 10, color: "#555" }}>Hold: {r.metadata.holding_days}d</span>
                <span style={{ fontSize: 10, color: "#555" }}>{r.metadata.sector}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Symbol setup */}
      {setupData && (
        <div>
          <h3 style={{ color: "#e0e0e0", fontSize: 16, marginBottom: 12 }}>
            {setupData.symbol} Setup Analysis
          </h3>

          {setupData.currentPosition && (
            <div style={{ background: "#6c5ce720", border: "1px solid #6c5ce740", borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ color: "#6c5ce7", fontWeight: 600, fontSize: 13 }}>Currently Held</div>
              <div style={{ fontSize: 12, color: "#a0a0b0" }}>
                Qty: {setupData.currentPosition.quantity} @ Rs{setupData.currentPosition.avgPrice?.toFixed(2)}
              </div>
            </div>
          )}

          <div style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>
            {setupData.historicalTrades} historical trades
          </div>

          {setupData.trades.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #3a3a4e" }}>
                  {["Action", "Price", "P&L", "Strategy", "Days", "Date"].map((h) => (
                    <th key={h} style={{ padding: "6px", textAlign: "left", color: "#888", fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {setupData.trades.map((t: any, i: number) => (
                  <tr key={i} style={{ borderBottom: "1px solid #22223a" }}>
                    <td style={{ padding: "6px" }}>
                      <span style={{ color: t.action === "BUY" ? "#6c5ce7" : "#ff5252" }}>{t.action}</span>
                    </td>
                    <td style={{ padding: "6px", color: "#a0a0b0" }}>Rs{t.price?.toFixed(2)}</td>
                    <td style={{ padding: "6px", color: (t.pnl || 0) >= 0 ? "#6c5ce7" : "#ff5252" }}>
                      {(t.pnl || 0) >= 0 ? "+" : ""}Rs{(t.pnl || 0).toFixed(0)}
                    </td>
                    <td style={{ padding: "6px", color: "#666" }}>{t.strategy}</td>
                    <td style={{ padding: "6px", color: "#666" }}>{t.holdingDays || 0}d</td>
                    <td style={{ padding: "6px", color: "#666" }}>
                      {t.entryDate ? new Date(t.entryDate).toLocaleDateString() : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Empty state */}
      {!loading && results.length === 0 && !setupData && (
        <div style={{ color: "#666", textAlign: "center", padding: 40 }}>
          <p style={{ fontSize: 14, margin: "0 0 8px" }}>Search historical trade setups with AI</p>
          <p style={{ fontSize: 12, margin: 0 }}>Try: "gap fill winners" or look up a symbol</p>
        </div>
      )}
    </div>
  );
}
